/**
 * What every screen needs: the firm (GET /firm), the app's runtime config, and who's
 * signed in (GET /customer/me). Loaded once when the app opens.
 */
import { ApiError, get, onSignedOut, post } from "@taxi/mobile-web/api";
import { applyColours, loadAppConfig } from "@taxi/mobile-web/config";
import { closeRealtime } from "@taxi/mobile-web/realtime";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import brand from "../../../../brand/brand.json";
import { forgetRecentPlaces } from "./draft";
import type { CustomerProfile, FirmInfo, PublicConfig } from "./types";

type Session = {
  firm: FirmInfo;
  config: PublicConfig;
  /** Null when signed out. */
  me: CustomerProfile | null;
  setMe: (me: CustomerProfile | null) => void;
  refreshMe: () => Promise<void>;
  signOut: () => Promise<void>;
  /** The number customers call, as people write it. */
  firmPhone: string;
};

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession outside SessionProvider");
  return session;
}

/** For screens that are only reachable when signed in. */
export function useMe(): CustomerProfile {
  const { me } = useSession();
  if (!me) throw new Error("Not signed in");
  return me;
}

type Loaded = { firm: FirmInfo; config: PublicConfig; me: CustomerProfile | null };

async function load(): Promise<Loaded> {
  const [firm, config, me] = await Promise.all([
    get<FirmInfo>("/firm"),
    loadAppConfig(),
    get<CustomerProfile>("/customer/me").catch((error) => {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }),
  ]);
  return { firm, config, me };
}

export function SessionProvider({
  children,
  loading,
  failed,
}: {
  children: ReactNode;
  loading: ReactNode;
  failed: (retry: () => void) => ReactNode;
}) {
  const [state, setState] = useState<Loaded | null>(null);
  const [error, setError] = useState(false);

  const start = useCallback(() => {
    setError(false);
    load()
      .then((loaded) => {
        applyColours(loaded.firm.colours);
        setState(loaded);
      })
      .catch(() => setError(true));
  }, []);

  useEffect(start, [start]);

  // Signed out somewhere else, or the session ended: back to the start.
  useEffect(
    () =>
      onSignedOut(() => {
        closeRealtime();
        setState((current) => (current ? { ...current, me: null } : current));
      }),
    [],
  );

  const setMe = useCallback(
    (me: CustomerProfile | null) => setState((current) => (current ? { ...current, me } : current)),
    [],
  );

  const refreshMe = useCallback(async () => {
    const me = await get<CustomerProfile>("/customer/me");
    setMe(me);
  }, [setMe]);

  const signOut = useCallback(async () => {
    closeRealtime();
    await post("/auth/logout").catch(() => undefined);
    try {
      sessionStorage.clear();
    } catch {
      // Storage is off: nothing kept.
    }
    forgetRecentPlaces();
    setMe(null);
  }, [setMe]);

  if (error) return <>{failed(start)}</>;
  if (!state) return <>{loading}</>;
  return (
    <SessionContext.Provider
      value={{
        ...state,
        setMe,
        refreshMe,
        signOut,
        firmPhone: state.firm.phone || brand.contact.phone,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}
